import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SUGGESTED_PROMPTS = [
  'What are our top competitors in Mexico right now?',
  'Which accounts should I prioritize this week?',
  'Draft a LinkedIn post about Trust Vision for fintechs',
  'What approved claims can I use in a campaign?',
  'Generate a campaign brief for Brazil fintechs Q2',
  "What's pending approval right now?",
  'Show me our geo availability matrix',
  'Which playbook should I use for bank onboarding?',
];

const ACTION_STYLES = {
  navigate:    { bg: '#EFF6FF', border: '#BFDBFE', color: '#1D4ED8', icon: '\u2192' },
  create_task: { bg: '#F0FDF4', border: '#BBF7D0', color: '#15803D', icon: '\u26A1' },
  open_record: { bg: '#F5F3FF', border: '#DDD6FE', color: '#6D28D9', icon: '\u{1F4C4}' },
};

const PLAY_ICONS = {
  target: '\uD83C\uDFAF', pencil: '\u270F\uFE0F', chart: '\uD83D\uDCCA',
  eye: '\uD83D\uDC41\uFE0F', calendar: '\uD83D\uDCC5', check: '\u2705',
};
const PLAY_CATEGORY_COLORS = {
  accounts: { bg: '#FFF7ED', border: '#FED7AA', color: '#C2410C' },
  content: { bg: '#EFF6FF', border: '#BFDBFE', color: '#1D4ED8' },
  competitive: { bg: '#FEF2F2', border: '#FECACA', color: '#B91C1C' },
  campaigns: { bg: '#F0FDF4', border: '#BBF7D0', color: '#15803D' },
};

// --- Floating Bubble ---
export function GTMAgentBubble() {
  const [open, setOpen] = useState(false);
  return (
    <>
      {!open && (
        <button className="_gtm-bubble" onClick={() => setOpen(true)} data-testid="agent-bubble-btn">
          <div className="_gtm-bubble-avatar">G</div>
          <span className="_gtm-bubble-label">GTM Agent</span>
          <span className="_gtm-bubble-dot"></span>
        </button>
      )}
      {open && (
        <div className="_gtm-slide-panel">
          <GTMChatPanel onClose={() => setOpen(false)} mode="slide" />
        </div>
      )}
      <BubbleStyles />
    </>
  );
}

// --- Sidebar Panel (for split screen) ---
export function GTMAgentSidebar({ onClose }) {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <GTMChatPanel onClose={onClose} mode="sidebar" />
    </div>
  );
}

// --- Full Page ---
export default function GTMAgent() {
  const [historyOpen, setHistoryOpen] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [chatKey, setChatKey] = useState(0);
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadSessions = () => {
    fetch(`${API_BASE}/api/agent/sessions`, { headers }).then(r => r.json()).then(data => {
      const byDate = {};
      (data || []).forEach(s => {
        const date = s.created_at?.split('T')[0] || 'Today';
        if (!byDate[date]) byDate[date] = [];
        byDate[date].push(s);
      });
      setSessions(byDate);
    }).catch(() => {});
  };

  useEffect(() => { loadSessions(); }, [token]);

  const startNewConversation = () => {
    setChatKey(k => k + 1);
  };

  return (
    <div className="_gp-root">
      {/* History sidebar */}
      {historyOpen && (
        <aside className="_gp-history">
          <div className="_gp-history-header">
            <div className="_gp-history-logo">
              <div className="_gp-logo-mark">G</div>
              <span>GTM Agent</span>
            </div>
            <button className="_gp-icon-btn" onClick={() => setHistoryOpen(false)} title="Collapse">{'\u2190'}</button>
          </div>
          <button className="_gp-new-chat-btn" onClick={startNewConversation}>
            <span>+</span> New conversation
          </button>
          <div className="_gp-history-list">
            {Object.entries(sessions).map(([date, items]) => (
              <div key={date}>
                <div className="_gp-history-date">{date === new Date().toISOString().split('T')[0] ? 'Today' : date}</div>
                {items.slice(0, 5).map(s => (
                  <button key={s.id} className="_gp-history-item">
                    {s.user_message?.slice(0, 38) || 'Conversation'}
                  </button>
                ))}
              </div>
            ))}
            {Object.keys(sessions).length === 0 && (
              <div className="_gp-history-empty">No conversations yet.<br />Start by asking a question.</div>
            )}
          </div>
        </aside>
      )}

      {/* Main chat */}
      <main className="_gp-main">
        {!historyOpen && (
          <button className="_gp-expand-btn" onClick={() => setHistoryOpen(true)} title="Expand history">{'\u2192'}</button>
        )}
        <GTMChatPanel key={chatKey} mode="fullpage" />
      </main>

      <FullPageStyles />
    </div>
  );
}

// --- Core Chat Panel (shared by all modes) ---
function GTMChatPanel({ onClose, mode = 'fullpage' }) {
  const { token, activeCountry } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [executing, setExecuting] = useState(null);
  const [quickPlays, setQuickPlays] = useState(null);
  const messagesEnd = useRef(null);
  const inputRef = useRef(null);
  const isCompact = mode === 'slide' || mode === 'sidebar';
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch quick plays on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/agent/quick-plays`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setQuickPlays(data.plays || []))
      .catch(() => setQuickPlays(null));
  }, [token]);

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: 'welcome', role: 'assistant', isWelcome: true,
        content: `Hi! I'm **GTM Agent** — your AI copilot grounded in all your GTM system data. I can answer questions, find evidence, and execute tasks with your approval.`,
        sources: [], confidence: 'high', suggested_actions: [],
        follow_up_questions: SUGGESTED_PROMPTS.slice(0, isCompact ? 3 : 4),
        timestamp: new Date().toISOString()
      }]);
    }
  }, []);

  const send = useCallback(async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    setInput('');
    setLoading(true);
    setMessages(p => [...p, { id: Date.now(), role: 'user', content: q, timestamp: new Date().toISOString() }]);
    try {
      const history = messages.filter(m => !m.isWelcome).slice(-6).map(m => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API_BASE}/api/agent/chat`, {
        method: 'POST', headers,
        body: JSON.stringify({ message: q, session_id: sessionId, history })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Server error (${res.status})`);
      }
      const data = await res.json();
      if (!data || !data.message || !data.message.trim()) {
        throw new Error('Empty response from agent. Please try rephrasing your question.');
      }
      setSessionId(data.session_id);
      setMessages(p => [...p, {
        id: Date.now() + 1, role: 'assistant',
        content: data.message, sources: data.sources || [],
        confidence: data.confidence, query_type: data.query_type,
        suggested_actions: data.suggested_actions || [],
        follow_up_questions: data.follow_up_questions || [],
        timestamp: new Date().toISOString()
      }]);
    } catch (err) {
      console.error('Agent chat error:', err);
      setMessages(p => [...p, { id: Date.now() + 1, role: 'assistant', content: err.message || 'Connection error. Please check your OpenAI key in Integration Settings.', sources: [], suggested_actions: [], follow_up_questions: [], timestamp: new Date().toISOString() }]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [input, loading, messages, sessionId, token]);

  const execAction = async (action) => {
    if (action.action_type === 'navigate' && action.route) {
      navigate(action.route); if (onClose) onClose(); return;
    }
    setExecuting(action.label);
    try {
      const res = await fetch(`${API_BASE}/api/agent/execute`, {
        method: 'POST', headers,
        body: JSON.stringify({ action_type: action.action_type, task_type: action.task_type, payload: action.payload || {}, route: action.route })
      });
      const data = await res.json();
      setMessages(p => [...p, {
        id: Date.now(), role: 'assistant',
        content: `\u2713 **${action.label}** executed successfully.${data.task ? ` Task \`${data.task.task_id}\` created.` : ''}`,
        sources: ['Task Bus'], confidence: 'high',
        suggested_actions: action.route ? [{ label: 'View Result', action_type: 'navigate', route: action.route }] : [],
        follow_up_questions: [], timestamp: new Date().toISOString()
      }]);
      if (action.route) setTimeout(() => navigate(action.route), 1000);
    } catch { /* silent */ }
    setExecuting(null);
  };

  const clearChat = async () => {
    await fetch(`${API_BASE}/api/agent/sessions`, { method: 'DELETE', headers });
    setMessages([]); setSessionId(null);
  };

  const md = (t) => t
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code style="background:#F1F5F9;padding:1px 5px;border-radius:3px;font-size:0.9em">$1</code>')
    .replace(/\n/g, '<br/>');

  return (
    <div className={`_cp-root ${isCompact ? 'compact' : 'full'}`}>
      {/* Header */}
      {isCompact && (
        <div className="_cp-header">
          <div className="_cp-header-left">
            <div className="_cp-hdr-avatar">G</div>
            <div>
              <div className="_cp-hdr-name">GTM Agent</div>
              <div className="_cp-hdr-status"><span className="_cp-online"></span>{activeCountry?.flag} {activeCountry?.name} \u00b7 grounded</div>
            </div>
          </div>
          <div className="_cp-hdr-actions">
            <button className="_cp-hdr-btn" onClick={clearChat} title="Clear">\uD83D\uDDD1</button>
            {onClose && <button className="_cp-hdr-btn" onClick={onClose} title="Close">\u2715</button>}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="_cp-messages">
        {/* Empty welcome state */}
        {messages.length <= 1 && messages[0]?.isWelcome && !isCompact && (
          <div className="_cp-welcome-state">
            <div className="_cp-welcome-avatar">G</div>
            <h2 className="_cp-welcome-title">GTM Agent</h2>
            <p className="_cp-welcome-sub">Grounded in your {activeCountry?.flag} {activeCountry?.name || 'Mexico'} GTM data.<br/>Ask me anything about your system.</p>
            <div className="_cp-suggested-grid">
              {quickPlays === null ? (
                // Fallback to static prompts
                SUGGESTED_PROMPTS.slice(0, 6).map((p, i) => (
                  <button key={i} className="_cp-suggest-card" onClick={() => send(p)}>{p}</button>
                ))
              ) : quickPlays.length === 0 ? (
                // Loading skeleton
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="_cp-play-skeleton" />
                ))
              ) : (
                quickPlays.slice(0, 6).map((play) => {
                  const cat = PLAY_CATEGORY_COLORS[play.category] || PLAY_CATEGORY_COLORS.accounts;
                  return (
                    <button key={play.id} className="_cp-play-card"
                      style={{ borderColor: cat.border }}
                      onClick={() => {
                        if (play.execution_mode === 'direct') {
                          execAction({ label: play.title, action_type: play.action_type, task_type: play.task_type, payload: play.payload });
                        } else {
                          send(play.prompt || play.title);
                        }
                      }}>
                      <div className="_cp-play-icon">{PLAY_ICONS[play.icon] || play.icon || '\uD83C\uDFAF'}</div>
                      <div className="_cp-play-text">
                        <div className="_cp-play-title">{play.title}</div>
                        <div className="_cp-play-desc">{play.description}</div>
                      </div>
                      <span className="_cp-play-badge" style={{ background: cat.bg, color: cat.color }}>{play.category}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {messages.map(msg => (
          <div key={msg.id} className={`_cp-msg-row ${msg.role}`}>
            {msg.role === 'assistant' && (
              <div className="_cp-msg-avatar">G</div>
            )}
            <div className="_cp-msg-body">
              <div className={`_cp-bubble ${msg.role}`} dangerouslySetInnerHTML={{ __html: md(msg.content) }} />
              {/* Sources */}
              {msg.role === 'assistant' && msg.sources?.length > 0 && (
                <div className="_cp-sources">
                  <span className="_cp-sources-icon">&#128206;</span>
                  {msg.sources.join(' \u00b7 ')}
                  {msg.confidence && <span className={`_cp-conf _cp-conf-${msg.confidence}`}>{msg.confidence}</span>}
                </div>
              )}
              {/* Action buttons */}
              {msg.suggested_actions?.length > 0 && (
                <div className="_cp-actions">
                  {msg.suggested_actions.map((a, i) => {
                    const s = ACTION_STYLES[a.action_type] || ACTION_STYLES.navigate;
                    return (
                      <button key={i} className="_cp-action-btn"
                        style={{ background: s.bg, borderColor: s.border, color: s.color }}
                        onClick={() => execAction(a)}
                        disabled={executing === a.label}
                        title={a.description}
                        data-testid={`agent-action-${i}`}>
                        <span>{s.icon}</span>
                        <span>{executing === a.label ? 'Working...' : a.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {/* Follow-up */}
              {msg.follow_up_questions?.length > 0 && (
                <div className="_cp-followups">
                  {msg.follow_up_questions.map((q, i) => (
                    <button key={i} className="_cp-followup" onClick={() => send(q)}>{q}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="_cp-msg-row assistant">
            <div className="_cp-msg-avatar">G</div>
            <div className="_cp-msg-body">
              <div className="_cp-bubble assistant _cp-typing">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEnd} />
      </div>

      {/* Input */}
      <div className="_cp-input-area">
        <div className="_cp-input-wrap">
          <textarea
            ref={inputRef}
            className="_cp-textarea"
            placeholder="Ask GTM Agent anything about your GTM motion..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            disabled={loading}
            data-testid="agent-input"
          />
          <button
            className={`_cp-send ${loading || !input.trim() ? 'disabled' : ''}`}
            onClick={() => send()}
            disabled={loading || !input.trim()}
            data-testid="agent-send-btn"
          >
            {loading ? <span className="_cp-send-spin"></span> : '\u2191'}
          </button>
        </div>
        <div className="_cp-input-hint">Enter to send \u00b7 Shift+Enter for new line \u00b7 Grounded in your system data</div>
      </div>

      <ChatStyles compact={isCompact} />
    </div>
  );
}

function ChatStyles({ compact }) {
  return <style>{`
    ._cp-root { display:flex; flex-direction:column; height:100%; background:#fff; font-family:'Inter',sans-serif; }
    ._cp-root.compact { height:100%; }

    /* Header (compact only) */
    ._cp-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; background:linear-gradient(135deg,#183763,#0276F1); flex-shrink:0; }
    ._cp-header-left { display:flex; align-items:center; gap:10px; }
    ._cp-hdr-avatar { width:34px; height:34px; background:rgba(255,255,255,0.2); border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:15px; font-weight:800; color:white; }
    ._cp-hdr-name { font-size:14px; font-weight:700; color:white; }
    ._cp-hdr-status { font-size:11px; color:rgba(255,255,255,0.75); display:flex; align-items:center; gap:4px; }
    ._cp-online { width:6px; height:6px; background:#34D399; border-radius:50%; }
    ._cp-hdr-actions { display:flex; gap:4px; }
    ._cp-hdr-btn { background:rgba(255,255,255,0.15); border:none; color:white; width:28px; height:28px; border-radius:6px; cursor:pointer; font-size:13px; display:flex; align-items:center; justify-content:center; }
    ._cp-hdr-btn:hover { background:rgba(255,255,255,0.25); }

    /* Messages */
    ._cp-messages { flex:1; overflow-y:auto; padding:${compact ? '12px' : '32px 24px'}; display:flex; flex-direction:column; gap:${compact ? '10px' : '20px'}; background:#fff; }

    /* Welcome state (fullpage only) */
    ._cp-welcome-state { display:flex; flex-direction:column; align-items:center; text-align:center; padding:40px 24px 0; }
    ._cp-welcome-avatar { width:64px; height:64px; background:linear-gradient(135deg,#183763,#0276F1); border-radius:18px; display:flex; align-items:center; justify-content:center; font-size:28px; font-weight:800; color:white; margin-bottom:16px; box-shadow:0 8px 24px rgba(2,118,241,0.25); }
    ._cp-welcome-title { font-size:26px; font-weight:800; color:#212F46; margin:0 0 8px; }
    ._cp-welcome-sub { font-size:15px; color:#53759E; margin:0 0 32px; line-height:1.6; }
    ._cp-suggested-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; max-width:640px; width:100%; }
    ._cp-suggest-card { background:#F8FBFF; border:1.5px solid #C8DFF5; border-radius:10px; padding:12px 14px; font-size:13px; color:#212F46; cursor:pointer; text-align:left; transition:all .15s; font-family:inherit; }
    ._cp-suggest-card:hover { background:#DBEDFF; border-color:#0276F1; color:#0276F1; }

    /* Quick Play cards */
    ._cp-play-card { display:flex; align-items:flex-start; gap:10px; background:#fff; border:1.5px solid #E2E8F0; border-radius:10px; padding:12px 14px; cursor:pointer; text-align:left; transition:all .15s; font-family:inherit; }
    ._cp-play-card:hover { background:#F8FAFC; transform:translateY(-1px); box-shadow:0 2px 8px rgba(0,0,0,0.06); }
    ._cp-play-icon { font-size:22px; line-height:1; flex-shrink:0; margin-top:1px; }
    ._cp-play-text { flex:1; min-width:0; }
    ._cp-play-title { font-size:13px; font-weight:600; color:#1E293B; margin-bottom:2px; }
    ._cp-play-desc { font-size:11.5px; color:#64748B; line-height:1.4; }
    ._cp-play-badge { font-size:10px; font-weight:600; padding:2px 7px; border-radius:6px; flex-shrink:0; text-transform:capitalize; margin-top:2px; }
    ._cp-play-skeleton { background:linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%); background-size:200% 100%; animation:shimmer 1.5s infinite; border-radius:10px; height:64px; }
    @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

    /* Message rows */
    ._cp-msg-row { display:flex; gap:${compact ? '8px' : '12px'}; max-width:${compact ? '100%' : '760px'}; margin:0 auto; width:100%; }
    ._cp-msg-row.user { flex-direction:row-reverse; }
    ._cp-msg-avatar { width:${compact ? '28px' : '34px'}; height:${compact ? '28px' : '34px'}; background:linear-gradient(135deg,#183763,#0276F1); border-radius:${compact ? '8px' : '10px'}; display:flex; align-items:center; justify-content:center; font-size:${compact ? '12px' : '14px'}; font-weight:800; color:white; flex-shrink:0; margin-top:2px; }
    ._cp-msg-body { max-width:${compact ? '85%' : '78%'}; display:flex; flex-direction:column; gap:6px; }
    ._cp-msg-row.user ._cp-msg-body { align-items:flex-end; }

    /* Bubbles */
    ._cp-bubble { padding:${compact ? '9px 12px' : '12px 16px'}; border-radius:${compact ? '12px' : '16px'}; font-size:${compact ? '13px' : '14px'}; line-height:1.65; color:#212F46; word-break:break-word; }
    ._cp-bubble.assistant { background:#F8FBFF; border:1px solid #E4F0FC; border-radius:4px ${compact ? '12px 12px 12px' : '16px 16px 16px'}; }
    ._cp-bubble.user { background:#0276F1; color:white; border-radius:${compact ? '12px 12px 4px 12px' : '16px 16px 4px 16px'}; }

    /* Typing */
    ._cp-typing { display:flex; gap:5px; align-items:center; padding:14px 16px !important; }
    ._cp-typing span { width:7px; height:7px; background:#8FA8C8; border-radius:50%; animation:_gtm-bounce 1.4s infinite; }
    ._cp-typing span:nth-child(2){animation-delay:.2s} ._cp-typing span:nth-child(3){animation-delay:.4s}
    @keyframes _gtm-bounce{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-6px)}}

    /* Sources */
    ._cp-sources { font-size:11px; color:#8FA8C8; display:flex; align-items:center; gap:5px; padding:0 2px; flex-wrap:wrap; }
    ._cp-sources-icon { font-size:10px; }
    ._cp-conf { padding:1px 6px; border-radius:100px; font-size:10px; font-weight:600; }
    ._cp-conf-high { background:#D1FAE5; color:#065F46; }
    ._cp-conf-medium { background:#FEF3C7; color:#92400E; }
    ._cp-conf-low { background:#FEE2E2; color:#B91C1C; }

    /* Actions */
    ._cp-actions { display:flex; flex-wrap:wrap; gap:6px; }
    ._cp-action-btn { display:flex; align-items:center; gap:5px; padding:6px 12px; border-radius:8px; border:1.5px solid; font-size:12px; font-weight:600; cursor:pointer; transition:opacity .15s; font-family:inherit; }
    ._cp-action-btn:hover { opacity:.8; }
    ._cp-action-btn:disabled { opacity:.5; cursor:wait; }

    /* Follow-ups */
    ._cp-followups { display:flex; flex-wrap:wrap; gap:5px; }
    ._cp-followup { background:#F8FBFF; border:1px solid #C8DFF5; border-radius:100px; padding:4px 11px; font-size:11px; color:#0276F1; cursor:pointer; font-family:inherit; transition:all .15s; }
    ._cp-followup:hover { background:#0276F1; color:white; border-color:#0276F1; }

    /* Input */
    ._cp-input-area { padding:${compact ? '10px 12px 10px' : '16px 24px 20px'}; background:white; border-top:1px solid #E4F0FC; flex-shrink:0; }
    ._cp-input-wrap { display:flex; align-items:flex-end; gap:8px; background:#F8FBFF; border:1.5px solid #C8DFF5; border-radius:12px; padding:8px 10px 8px 14px; transition:border-color .2s, box-shadow .2s; }
    ._cp-input-wrap:focus-within { border-color:#0276F1; box-shadow:0 0 0 3px rgba(2,118,241,.08); background:white; }
    ._cp-textarea { flex:1; border:none; background:transparent; font-size:14px; color:#212F46; resize:none; outline:none; font-family:inherit; line-height:1.5; max-height:120px; overflow-y:auto; }
    ._cp-textarea::placeholder { color:#8FA8C8; }
    ._cp-send { width:34px; height:34px; background:#0276F1; color:white; border:none; border-radius:8px; cursor:pointer; font-size:18px; font-weight:700; display:flex; align-items:center; justify-content:center; transition:background .15s; flex-shrink:0; }
    ._cp-send:hover:not(.disabled) { background:#005DC0; }
    ._cp-send.disabled { background:#C8DFF5; cursor:not-allowed; }
    ._cp-send-spin { width:14px; height:14px; border:2px solid rgba(255,255,255,.3); border-top-color:white; border-radius:50%; animation:_gtm-spin .8s linear infinite; }
    @keyframes _gtm-spin{to{transform:rotate(360deg)}}
    ._cp-input-hint { font-size:10px; color:#8FA8C8; text-align:center; margin-top:6px; }
  `}</style>;
}

function FullPageStyles() {
  return <style>{`
    ._gp-root { display:flex; height:100vh; background:#fff; font-family:'Inter',sans-serif; overflow:hidden; }
    ._gp-history { width:260px; flex-shrink:0; background:#F8FBFF; border-right:1px solid #E4F0FC; display:flex; flex-direction:column; overflow:hidden; }
    ._gp-history-header { display:flex; align-items:center; justify-content:space-between; padding:16px 14px 12px; border-bottom:1px solid #E4F0FC; }
    ._gp-history-logo { display:flex; align-items:center; gap:10px; font-size:14px; font-weight:700; color:#212F46; }
    ._gp-logo-mark { width:30px; height:30px; background:linear-gradient(135deg,#183763,#0276F1); border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:800; color:white; }
    ._gp-icon-btn { background:none; border:none; color:#8FA8C8; cursor:pointer; font-size:16px; padding:4px; border-radius:4px; }
    ._gp-icon-btn:hover { color:#212F46; background:#E4F0FC; }
    ._gp-new-chat-btn { margin:10px 10px 6px; padding:9px 14px; background:white; border:1.5px dashed #C8DFF5; border-radius:9px; color:#0276F1; font-size:13px; font-weight:600; cursor:pointer; display:flex; align-items:center; gap:8px; transition:all .15s; font-family:inherit; }
    ._gp-new-chat-btn:hover { border-color:#0276F1; background:#EFF6FF; }
    ._gp-history-list { flex:1; overflow-y:auto; padding:4px 8px 12px; }
    ._gp-history-date { font-size:10px; font-weight:700; color:#8FA8C8; text-transform:uppercase; letter-spacing:1px; padding:10px 6px 4px; }
    ._gp-history-item { width:100%; text-align:left; padding:7px 10px; border:none; background:transparent; border-radius:7px; cursor:pointer; font-size:12px; color:#53759E; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-family:inherit; transition:all .15s; }
    ._gp-history-item:hover { background:white; color:#212F46; }
    ._gp-history-empty { padding:20px 10px; text-align:center; font-size:12px; color:#8FA8C8; line-height:1.6; }
    ._gp-main { flex:1; display:flex; flex-direction:column; overflow:hidden; position:relative; }
    ._gp-expand-btn { position:absolute; top:16px; left:12px; z-index:10; background:#F8FBFF; border:1px solid #E4F0FC; color:#53759E; border-radius:6px; padding:4px 8px; cursor:pointer; font-size:13px; }
    ._gp-expand-btn:hover { color:#212F46; }
  `}</style>;
}

function BubbleStyles() {
  return <style>{`
    ._gtm-bubble { position:fixed; bottom:24px; right:24px; z-index:9999; display:flex; align-items:center; gap:8px; background:linear-gradient(135deg,#183763,#0276F1); color:white; border:none; border-radius:100px; padding:11px 18px 11px 12px; cursor:pointer; box-shadow:0 4px 20px rgba(2,118,241,.4); font-size:14px; font-weight:700; font-family:'Inter',sans-serif; transition:all .2s; }
    ._gtm-bubble:hover { transform:translateY(-2px); box-shadow:0 6px 24px rgba(2,118,241,.5); }
    ._gtm-bubble-avatar { width:28px; height:28px; background:rgba(255,255,255,.2); border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:800; }
    ._gtm-bubble-label { color:white; }
    ._gtm-bubble-dot { width:8px; height:8px; background:#34D399; border-radius:50%; }
    ._gtm-slide-panel { position:fixed; bottom:24px; right:24px; z-index:9999; width:420px; height:600px; border-radius:16px; overflow:hidden; box-shadow:0 20px 60px rgba(24,55,99,.25); animation:_gtm-slideup .2s ease; }
    @keyframes _gtm-slideup{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
  `}</style>;
}
